import React from 'react';

const SCRIPT_ID = 'google-maps-places-script';
// PlaceAutocompleteElement currently allows max 15 included_region_codes.
// To support the full EU set, we use a geographic restriction around Europe.
// Mediterrán / EU + észak-afrikai felvételek (pl. Algéria); a korábbi déli 34° sok helyt kizárt.
const EUROPE_BOUNDS = {
  south: 18.0,
  west: -11.0,
  north: 72.0,
  east: 42.0,
};

/**
 * A PlaceAutocompleteElement csak importLibrary('places') után érhető el.
 * A script `load` eseménye gyakran a `importLibrary` megjelenése ELŐTT fut (főleg loading=async-mal),
 * és a második autocomplete már le is csúszhat a `load` eseményről — ezért megvárjuk pollal.
 */
let mapsBootstrapPromise = null;

function waitForImportLibrary(timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }
      if (Date.now() - t0 > timeoutMs) {
        reject(
          new Error(
            'Google Maps API nem inicializálódott időben. Nézd a böngésző konzolt (kulcs, referrer), majd frissíts (Ctrl+Shift+R).',
          ),
        );
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

function loadMapsBootstrap(apiKey) {
  if (!apiKey) return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
  if (window.google?.maps?.importLibrary) return Promise.resolve();

  if (!mapsBootstrapPromise) {
    mapsBootstrapPromise = (async () => {
      const existing = document.getElementById(SCRIPT_ID);
      if (!existing) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.id = SCRIPT_ID;
          script.async = true;
          // Ne használjunk loading=async a URL-ben: a load és az importLibrary között versenyhelyzet van.
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&language=hu`;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Google Maps script failed to load'));
          document.head.appendChild(script);
        });
      }
      await waitForImportLibrary();
    })().catch((err) => {
      mapsBootstrapPromise = null;
      throw err;
    });
  }

  return mapsBootstrapPromise;
}

function importPlacesLibrary() {
  if (!window.google?.maps?.importLibrary) {
    return Promise.reject(new Error('Google Maps még nem töltődött be.'));
  }
  return window.google.maps.importLibrary('places');
}

function parseAddress(place) {
  const components = place.addressComponents || [];
  const byType = (type) => components.find((c) => c.types && c.types.includes(type));
  const route = byType('route')?.longText || '';
  const number = byType('street_number')?.longText || '';
  const city =
    byType('locality')?.longText ||
    byType('postal_town')?.longText ||
    byType('administrative_area_level_2')?.longText ||
    '';
  const zip = byType('postal_code')?.longText || '';
  const country = byType('country')?.longText || '';

  return {
    street: [route, number].filter(Boolean).join(' ').trim(),
    city,
    zip,
    country,
    formatted: place.formattedAddress || '',
    placeId: place.id || place.placeId || '',
  };
}

export default function GoogleAddressAutocomplete({
  id,
  value,
  onResolved,
  label = 'Cím keresése *',
  /** Ha megadod, ez lesz a gyökér `className` (pl. `field compact …`). Egyébként: quote űrlap layout. */
  fieldClassName,
  /** Landing hero: teljesen lekerekített keret a komponensnek. */
  pillRounded = false,
}) {
  const hostRef = React.useRef(null);
  const elementRef = React.useRef(null);
  const onResolvedRef = React.useRef(onResolved);
  const [state, setState] = React.useState({ loading: false, error: '' });

  onResolvedRef.current = onResolved;

  const rootClassName =
    fieldClassName != null && String(fieldClassName).trim() !== ''
      ? fieldClassName
      : 'field qf-field qf-field--full';

  React.useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const host = hostRef.current;
    if (!apiKey || !host) return undefined;

    let cancelled = false;
    setState({ loading: true, error: '' });
    loadMapsBootstrap(apiKey)
      .then(() => importPlacesLibrary())
      .then((placesLib) => {
        if (cancelled || !hostRef.current) return;
        const PlaceAutocompleteElement = placesLib?.PlaceAutocompleteElement;
        if (!PlaceAutocompleteElement) {
          throw new Error(
            'PlaceAutocompleteElement nem érhető el. Google Cloud: Maps JavaScript API + Places API (New), számlázás, API-kulcs korlátozások (HTTP referrerek).',
          );
        }

        const el = new PlaceAutocompleteElement({
          locationRestriction: EUROPE_BOUNDS,
        });
        el.id = id;
        el.className = 'qf-google-autocomplete';
        // Force light theme directly on the web component host (shadow DOM-safe styling entrypoint).
        el.style.setProperty('color-scheme', 'light');
        el.style.setProperty('background-color', '#ffffff');
        el.style.setProperty('border', '1px solid #d1d5db');
        el.style.setProperty('border-radius', pillRounded ? '999px' : '8px');
        host.innerHTML = '';
        host.appendChild(el);
        elementRef.current = el;

        const onPlace = async (evt) => {
          const prediction = evt?.placePrediction;
          if (!prediction) return;
          const place = prediction.toPlace();
          await place.fetchFields({ fields: ['id', 'formattedAddress', 'addressComponents'] });
          onResolvedRef.current?.(parseAddress(place));
        };

        el.addEventListener('gmp-select', onPlace);
        el.addEventListener('gmp-placeselect', onPlace);
        setState({ loading: false, error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, error: e.message || 'Google Maps címkereső nem tölthető be.' });
      });

    return () => {
      cancelled = true;
      const el = elementRef.current;
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      elementRef.current = null;
      host.innerHTML = '';
    };
  }, [id, pillRounded]);

  const missingKey = !process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  return (
    <div className={rootClassName}>
      <label htmlFor={id}>{label}</label>
      <div ref={hostRef} aria-label="Google cím kereső" />
      <input type="hidden" value={value} readOnly />
      {state.loading && <small className="qf-help">Google címkereső betöltése…</small>}
      {missingKey && (
        <small className="qf-help qf-help--warn">Hiányzik: REACT_APP_GOOGLE_MAPS_API_KEY (frontend .env)</small>
      )}
      {!missingKey && state.error && <small className="qf-help qf-help--warn">{state.error}</small>}
    </div>
  );
}
